import { msg } from '@lingui/core/macro';

import { defineFilters } from '@/shared/filters/define-filters';
import { defineEntitySource } from '@/shared/form/entity-source';
import { documentInputRaw, maskDocumentInput } from '@/shared/format/document';

// Filtros das listas (`NaturaPersonAndLegalPersonFilter.tsx`, inventário §2.3), na ordem de rótulo do
// legado (`:45`). Entra na rota (JS inicial): só o que é leve.

const reference = () => import('@/shared/reference/reference.api');

/**
 * Correspondente do filtro PF: a lista de `GET /Tenant` que a casca já carrega (`tenantContext.tsx:52-86`),
 * filtrada no cliente pelo nome, como o autocomplete local do legado (`:151-160`). Import dinâmico: arquivo leve.
 */
export const tenantFilterSource = defineEntitySource<{ readonly id: string; readonly name: string }>({
  key: ['person', 'filter', 'tenant'],
  search: async (query, signal) => {
    const needle = query.trim().toLowerCase();
    return (await (await reference()).listTenantOptions(signal)).filter((item) => item.name.toLowerCase().includes(needle));
  },
  byId: async (id, signal) => (await (await reference()).listTenantOptions(signal)).find((item) => item.id === id),
  toOption: (item) => ({ value: item.id, label: item.name }),
});

const PHONE_MASK = '(00) 00000-0000';

/** `phoneNumberMask` do legado (`utils/constants/masks/phoneNumberMask.ts`): o fio leva o texto mascarado (U21). */
export function maskPhoneLikeLegacy(value: string): string {
  const digits = value.replace(/\D/g, '').slice(0, 11);
  let out = '';
  let index = 0;
  for (const slot of PHONE_MASK) {
    if (index >= digits.length) break;
    if (slot === '0') {
      out += digits.charAt(index);
      index += 1;
    } else {
      out += slot;
    }
  }
  return out;
}

/** `cpfMask` fixo do filtro PF (`:169`): o fio leva o CPF formatado (U21). */
export const maskCpfLikeLegacy = (raw: string) => maskDocumentInput(documentInputRaw(raw, { alphanumeric: false }).slice(0, 11));

/** `cnpjMask` do filtro PJ (`:169`), alfanumérico conforme a flag, como o legado (U21). */
export const maskCnpjLikeLegacy = (raw: string) => maskDocumentInput(documentInputRaw(raw), { legalPerson: true });

const period = {
  key: 'initialDate',
  to: 'finalDate',
  type: 'dateRange',
  label: msg`Data de criação`,
  fromLabel: msg`Data inicial`,
  toLabel: msg`Data final`,
} as const;

export const naturalPersonFilters = defineFilters([
  { key: 'searchString', type: 'search', label: msg`Todos`, placeholder: msg`Procurar` },
  { key: 'tenant', type: 'entity', label: msg`Correspondente`, source: tenantFilterSource },
  { key: 'registrationNumber', type: 'document', label: msg`CPF`, toParam: maskCpfLikeLegacy },
  period,
  { key: 'email', type: 'text', label: msg`Email` },
  { key: 'personDisplay', type: 'text', label: msg`Nome` },
  { key: 'phone', type: 'text', label: msg`Telefone`, toParam: maskPhoneLikeLegacy },
]);

export const legalPersonFilters = defineFilters([
  { key: 'searchString', type: 'search', label: msg`Todos`, placeholder: msg`Procurar` },
  { key: 'registrationNumber', type: 'document', label: msg`CNPJ`, toParam: maskCnpjLikeLegacy },
  period,
  { key: 'email', type: 'text', label: msg`Email` },
  { key: 'phone', type: 'text', label: msg`Telefone`, toParam: maskPhoneLikeLegacy },
  { key: 'companyName', type: 'text', label: msg`Razão social` },
]);

export type PersonFilterSet = typeof naturalPersonFilters | typeof legalPersonFilters;
